import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Link, browserHistory } from 'react-router'
import { Card } from 'components'
import { Spinner } from 'lp-components'
import { LEARNING_HUB_ROUTE } from 'config'
import * as apiActions from 'api-actions'
import { selectors as globalSelectors } from '../global-reducer'
import {
  isChapterCompleted,
  isCourseCompleted,
} from '../utils/block-navigation'
import { courseForumPath } from '../utils/course-forum-url'

const propTypes = {
  params: PropTypes.shape({
    courseId: PropTypes.string.isRequired,
  }).isRequired,
  currentCourse: PropTypes.object,
  fetchLearningHubCourse: PropTypes.func.isRequired,
}

const defaultProps = {
  currentCourse: undefined,
}

function ChapterCompleteIcon() {
  return (
    <span className="learning-hub-course-completion__chapter-icon" aria-hidden>
      <svg
        width={22}
        height={22}
        viewBox="0 0 22 22"
        xmlns="http://www.w3.org/2000/svg"
        className="learning-hub-course-completion__chapter-complete-svg"
      >
        <circle cx="11" cy="11" r="11" fill="#087B2E" />
        <path
          d="M6 11.4 L9.6 15 16 8.3"
          fill="none"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

function CourseCompletionPage({
  params,
  currentCourse,
  fetchLearningHubCourse,
}) {
  const courseId = Number(params.courseId)

  useEffect(() => {
    if (!Number.isNaN(courseId)) {
      fetchLearningHubCourse(courseId)
    }
  }, [courseId, fetchLearningHubCourse])

  const course =
    currentCourse && Number(currentCourse.id) === courseId
      ? currentCourse
      : undefined

  const chapters = useMemo(
    () =>
      [...(course?.chapters || [])].sort((a, b) => Number(a.id) - Number(b.id)),
    [course]
  )

  const certificationUrl = useMemo(() => {
    const raw = course?.certification
    return raw && String(raw).trim() ? String(raw).trim() : ''
  }, [course])

  const courseComplete = useMemo(
    () => (course ? isCourseCompleted(course) : null),
    [course]
  )

  useEffect(() => {
    if (!course || courseComplete === null) return
    if (!courseComplete) {
      browserHistory.replace(
        `${LEARNING_HUB_ROUTE}/course/${courseId}/chapters`
      )
    }
  }, [course, courseComplete, courseId])

  if (!course) {
    return (
      <div className="content-block-container learning-hub-course-completion">
        <Spinner />
      </div>
    )
  }

  if (courseComplete === false) {
    return (
      <div className="content-block-container learning-hub-course-completion">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="content-block-container learning-hub-course-completion">
      <nav
        className="learning-hub-course-completion__breadcrumb"
        aria-label="Breadcrumb"
      >
        <Link to={`${LEARNING_HUB_ROUTE}/course-catalog`}>Course Catalog</Link>
        <span>/</span>
        <span>{course.title}</span>
        <span>/</span>
        <span>Certificate</span>
      </nav>

      <header className="learning-hub-course-completion__header">
        <h1>{course.title}</h1>
        {course.instructor ? <p>{course.instructor}</p> : null}
      </header>

      <div className="learning-hub-course-completion__grid">
        <Card className="learning-hub-course-completion__sidebar">
          <h3 className="learning-hub-course-completion__sidebar-title">
            {course.title}
          </h3>
          <div className="learning-hub-course-completion__chapters-label">
            <span>Chapters</span>
            <span aria-hidden>▾</span>
          </div>

          <ul className="learning-hub-course-completion__chapter-list">
            {chapters.map((chapter, idx) => {
              const done = isChapterCompleted(chapter)
              return (
                <li
                  key={chapter.id}
                  className={`learning-hub-course-completion__chapter-row ${
                    done ? 'is-completed' : ''
                  }`}
                >
                  {done ? <ChapterCompleteIcon /> : null}
                  <span>{`Chapter ${idx + 1}`}</span>
                </li>
              )
            })}
          </ul>

          <button
            type="button"
            className="learning-hub-course-completion__forum-button"
            onClick={() => browserHistory.push(courseForumPath(course))}
          >
            Go to Course Forum
          </button>
        </Card>

        <section className="learning-hub-course-completion__main">
          <Card className="learning-hub-course-completion__card">
            <h2 className="learning-hub-course-completion__congrats">
              Congratulations!
            </h2>
            <p>You&apos;ve successfully completed {course.title}.</p>
            <p>
              Thank you for your time, effort, and engagement throughout the
              learning experience. By completing this course, you have expanded
              your knowledge, developed new skills, and taken another step
              forward in your personal and professional growth.
            </p>

            <div className="learning-hub-course-completion__actions">
              {certificationUrl ? (
                <a
                  href={certificationUrl}
                  className="learning-hub-course-completion__download-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  Download Certificate
                </a>
              ) : null}
              <Link
                to={`${LEARNING_HUB_ROUTE}/course-catalog`}
                className="learning-hub-course-completion__back-link"
              >
                Back to Courses
              </Link>
            </div>
          </Card>
        </section>
      </div>
    </div>
  )
}

CourseCompletionPage.propTypes = propTypes
CourseCompletionPage.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentCourse: globalSelectors.currentCourse(state),
  }
}

const mapDispatchToProps = {
  fetchLearningHubCourse: apiActions.fetchLearningHubCourse,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  CourseCompletionPage
)
